import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { THIN_SCROLLBAR } from 'common/styles/listUI';

const INK = '#383838';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const LINE_2 = '#eff0ee';
const CRITICAL = '#d92b2b';
const GREEN = '#91b846';

const CRITERIA = [
  { key: 'all', glyph: null, labelKey: 'assets.criteria.all', def: 'All', hot: true },
  { key: 'alarms', glyph: '⚠', labelKey: 'assets.criteria.alarms', def: 'Alarms' },
  { key: 'health_score', glyph: '♥', labelKey: 'assets.criteria.healthScore', def: 'Health Score' },
  { key: 'diagnostics', glyph: '⬡', labelKey: 'assets.criteria.diagnostics', def: 'Diagnostics' },
  { key: 'baselines_status', glyph: '◳', labelKey: 'assets.criteria.baselines', def: 'Baselines' },
  { key: 'missing_asset_info', glyph: '◷', labelKey: 'assets.criteria.missingInfo', def: 'Missing Info' },
  { key: 'tasks', glyph: '✓', labelKey: 'assets.criteria.tasks', def: 'Open Tasks' }
];
// Non-analyst (stripped) roles only see the actionable work buckets.
const STRIPPED_CRITERIA_KEYS = ['all', 'missing_asset_info', 'tasks'];

const Strip = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 20px;
  border-bottom: 1px solid ${LINE};
  background: white;
  overflow-x: auto;
  overflow-y: hidden;
  /* Keep a trackpad swipe across the criteria tabs from triggering
     browser back-navigation. */
  overscroll-behavior-x: contain;
  ${THIN_SCROLLBAR}
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 12.5px;
  cursor: pointer;
  padding: 9px 12px 8px;
  white-space: nowrap;
  color: ${props => (props.active ? INK : INK_2)};
  font-weight: ${props => (props.active ? 700 : 400)};
  border-bottom: 2px solid ${props => (props.active ? INK : 'transparent')};

  &:hover { color: ${INK}; }
  .glyph { color: ${INK_3}; }
`;

const Count = styled.span`
  font-size: 11px;
  font-weight: 700;
  border-radius: 9px;
  min-width: 22px;
  text-align: center;
  padding: 0 7px;
  line-height: 17px;
  background: ${props => (props.hot ? '#fcefef' : LINE_2)};
  color: ${props => (props.hot ? CRITICAL : INK_2)};
`;

const Right = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
  padding: 4px 0;
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  color: ${INK_2};

  b { color: ${INK}; }
  .bar { width: 80px; height: 5px; background: ${LINE_2}; border-radius: 3px; overflow: hidden; }
  .bar i { display: block; height: 100%; background: ${GREEN}; }
`;

const SnoozedPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid ${props => (props.active ? '#cfe0a8' : LINE)};
  background: ${props => (props.active ? '#eef3e2' : 'white')};
  color: ${props => (props.active ? '#53682c' : INK_2)};
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  padding: 3px 10px;
  cursor: pointer;
  white-space: nowrap;

  &:hover { border-color: ${INK_3}; }
  .num {
    font-weight: 700;
    background: ${props => (props.active ? 'white' : '#eff0ee')};
    color: ${props => (props.active ? '#53682c' : INK_2)};
    border-radius: 8px;
    padding: 0 6px;
  }
`;

const CriteriaStrip = ({
  byCriteria, triageProgress, totalCount, selectedCriteria, onSelectCriteria,
  snoozedCount, showSnoozed, onToggleSnoozed, stripped, t
}) => {
  const counts = byCriteria || {};
  const progress = triageProgress || {};
  const reviewed = progress.reviewed_count || 0;
  const total = totalCount != null ? totalCount : 0;
  let pct = 0;
  if (progress.reviewed_percent != null) {
    pct = progress.reviewed_percent;
  } else if (total > 0) {
    pct = (reviewed / total) * 100;
  }
  const criteria = stripped
    ? CRITERIA.filter(c => STRIPPED_CRITERIA_KEYS.includes(c.key))
    : CRITERIA;

  return (
    <Strip>
      {criteria.map((c) => {
        const count = c.key === 'all' ? total : (counts[c.key] || 0);
        const active = selectedCriteria === c.key;
        return (
          <Tab key={c.key} type="button" active={active} onClick={() => onSelectCriteria(c.key)}>
            {c.glyph && <span className="glyph">{c.glyph}</span>}
            {t(c.labelKey, { defaultValue: c.def })}
            <Count hot={c.hot}>{count}</Count>
          </Tab>
        );
      })}
      <Right>
        {snoozedCount > 0 && (
          <SnoozedPill type="button" active={showSnoozed} onClick={onToggleSnoozed}>
            {`🕐 ${t('assets.triage.snoozed', { defaultValue: 'Snoozed' })}`}
            <span className="num">{snoozedCount}</span>
          </SnoozedPill>
        )}
        <Progress>
          <span>
            {t('assets.criteria.reviewedByYou', { defaultValue: 'Reviewed by you' })}
            {' '}
            <b>{`${reviewed}/${total}`}</b>
          </span>
          <span className="bar"><i style={{ width: `${pct}%` }} /></span>
        </Progress>
      </Right>
    </Strip>
  );
};

CriteriaStrip.propTypes = {
  byCriteria: PropTypes.object,
  triageProgress: PropTypes.shape({
    reviewed_count: PropTypes.number,
    unreviewed_count: PropTypes.number,
    reviewed_percent: PropTypes.number
  }),
  totalCount: PropTypes.number,
  selectedCriteria: PropTypes.string,
  onSelectCriteria: PropTypes.func.isRequired,
  snoozedCount: PropTypes.number,
  showSnoozed: PropTypes.bool,
  onToggleSnoozed: PropTypes.func,
  stripped: PropTypes.bool,
  t: PropTypes.func.isRequired
};

CriteriaStrip.defaultProps = {
  byCriteria: {},
  triageProgress: {},
  totalCount: 0,
  selectedCriteria: 'all',
  snoozedCount: 0,
  showSnoozed: false,
  onToggleSnoozed: () => {},
  stripped: false
};

export default withTranslation()(CriteriaStrip);
